import { useState, useEffect } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sparkles, Sun, Moon, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Login() {
  const { user, login } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [appSettings, setAppSettings] = useState({ app_name: "ResumeForge", tagline: "Standardize any resume in seconds.", login_image_url: null });

  useEffect(() => {
    api.get("/settings/app").then((r) => setAppSettings(r.data)).catch(() => {});
  }, []);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid email or password");
    } finally { setBusy(false); }
  };

  const bgImage = appSettings.login_image_url ? `${BACKEND_URL}${appSettings.login_image_url}` : null;

  return (
    <div className="min-h-screen flex items-center justify-center bg-background text-foreground relative overflow-hidden px-4">
      {/* Backdrop */}
      {bgImage ? (
        <>
          <img src={bgImage} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-background/70 backdrop-blur-md" />
        </>
      ) : (
        <>
          <div className="absolute -top-32 -left-32 size-96 rounded-full bg-primary/10 blur-3xl" />
          <div className="absolute -bottom-32 -right-32 size-96 rounded-full bg-primary/10 blur-3xl" />
        </>
      )}

      <div className="absolute top-5 right-5 z-10">
        <Button variant="ghost" size="icon" onClick={toggle} data-testid="login-theme-toggle" className="lift">
          {theme === "dark" ? <Sun className="size-4" /> : <Moon className="size-4" />}
        </Button>
      </div>

      <div className="w-full max-w-md relative z-10 fade-up">
        <div className="flex items-center justify-center gap-2.5 mb-8">
          <div className="size-10 rounded-xl bg-primary text-primary-foreground flex items-center justify-center shadow-lg shadow-primary/20">
            <Sparkles className="size-5" strokeWidth={2.4} />
          </div>
          <div>
            <div className="display text-2xl font-extrabold tracking-tight leading-none">{appSettings.app_name || "ResumeForge"}</div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mt-1">AI Standardizer</div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-xl p-7 shadow-xl shadow-foreground/[0.03]">
          <h1 className="display text-2xl font-extrabold tracking-tight">Sign in to your workspace</h1>
          <p className="text-muted-foreground text-sm mt-1">{appSettings.tagline}</p>

          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-xs font-semibold">Work email</Label>
              <Input id="email" data-testid="login-email-input" type="email" required autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" className="mt-1.5 h-11" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-xs font-semibold">Password</Label>
              </div>
              <Input id="password" data-testid="login-password-input" type="password" required autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter your password" className="mt-1.5 h-11" />
            </div>
            <Button type="submit" size="lg" disabled={busy} data-testid="login-submit-button" className="w-full lift gap-2 h-11">
              {busy ? <Loader2 className="size-4 animate-spin" /> : <ArrowRight className="size-4" />}
              Sign in
            </Button>
          </form>

          <div className="text-xs text-muted-foreground text-center mt-5">
            New here? <Link to="/register" className="text-primary font-semibold hover:underline" data-testid="link-register">Create an account</Link>
          </div>
        </div>

        <div className="text-center text-[10px] uppercase tracking-[0.24em] text-muted-foreground mt-6">
          {appSettings.app_name || "ResumeForge"} · Premium SaaS · v1.0
        </div>
      </div>
    </div>
  );
}
